import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';

const POPOVER_BASE_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'fixed'
};

function focusWithoutPageScroll(element) {
  if (!element || typeof window === 'undefined') {
    return;
  }

  const scrollLeft = typeof window.scrollX === 'number' ? window.scrollX : window.pageXOffset;
  const scrollTop = typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset;

  try {
    element.focus({ preventScroll: true });
  } catch {
    // Older browsers reject FocusOptions. The exact scroll position is restored
    // below if their legacy focus implementation scrolls the page.
    element.focus();
  }

  const currentScrollLeft = typeof window.scrollX === 'number' ? window.scrollX : window.pageXOffset;
  const currentScrollTop = typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset;
  if (currentScrollLeft !== scrollLeft || currentScrollTop !== scrollTop) {
    window.scrollTo(scrollLeft, scrollTop);
  }
}

function ReportDimensionFilter({
  emptyMessageId = 'components.pages.reports.filters.noOptions',
  id,
  labelId,
  mode,
  onModeChange,
  onSelectionChange,
  options,
  selectedIds
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(POPOVER_BASE_STYLE);
  const [search, setSearch] = useState('');
  const menuRef = useRef(null);
  const pickerRef = useRef(null);
  const searchRef = useRef(null);
  const triggerRef = useRef(null);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const labelsById = useMemo(() => options.reduce((labels, option) => {
    labels[option.value] = option.selectedLabel || option.label;
    return labels;
  }, {}), [options]);
  const filteredOptions = normalizedSearch
    ? options.filter(option => [
      option.label,
      option.selectedLabel,
      option.groupLabel,
      option.categoryLabel
    ].filter(Boolean).some(label => label.toLocaleLowerCase().includes(normalizedSearch)))
    : options;
  const groupedOptions = useMemo(() => filteredOptions.reduce((categories, option) => {
    const categoryKey = option.categoryKey || '__default-category';
    let category = categories.find(candidate => candidate.key === categoryKey);
    if (!category) {
      category = { key: categoryKey, label: option.categoryLabel || '', groups: [] };
      categories.push(category);
    }

    const groupKey = option.groupId || '__default-group';
    let group = category.groups.find(candidate => candidate.key === groupKey);
    if (!group) {
      group = { key: groupKey, label: option.groupLabel || '', options: [] };
      category.groups.push(group);
    }
    group.options.push(option);
    return categories;
  }, []), [filteredOptions]);
  const selectedLabels = selectedIds.map(value => labelsById[value] || value);

  const positionPopover = useCallback(() => {
    if (!triggerRef.current || typeof window === 'undefined') {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const viewportGutter = 12;
    const anchorGap = 4;
    const availableWidth = Math.max(0, viewportWidth - (viewportGutter * 2));
    const width = Math.min(Math.max(triggerRect.width, 240), availableWidth);
    const maximumLeft = Math.max(viewportGutter, viewportWidth - width - viewportGutter);
    const left = Math.min(Math.max(triggerRect.left, viewportGutter), maximumLeft);
    const menuHeight = menuRef.current
      ? menuRef.current.scrollHeight || menuRef.current.offsetHeight
      : 0;
    const spaceBelow = Math.max(0, viewportHeight - viewportGutter - triggerRect.bottom - anchorGap);
    const spaceAbove = Math.max(0, triggerRect.top - anchorGap - viewportGutter);
    const opensAbove = menuHeight > spaceBelow && spaceAbove > spaceBelow;
    const availableHeight = opensAbove ? spaceAbove : spaceBelow;
    const renderedHeight = Math.min(menuHeight || availableHeight, availableHeight);
    const top = opensAbove
      ? Math.max(viewportGutter, triggerRect.top - anchorGap - renderedHeight)
      : Math.min(
        Math.max(viewportGutter, triggerRect.bottom + anchorGap),
        viewportHeight - viewportGutter
      );

    setPopoverStyle({
      ...POPOVER_BASE_STYLE,
      left: `${Math.round(left)}px`,
      maxHeight: `${Math.round(availableHeight)}px`,
      top: `${Math.round(top)}px`,
      width: `${Math.round(width)}px`
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      positionPopover();
    }
  }, [filteredOptions.length, isOpen, positionPopover]);

  useEffect(() => {
    if (mode === 'all' && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = event => {
      const isInsidePicker = pickerRef.current && pickerRef.current.contains(event.target);
      const isInsideMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!isInsidePicker && !isInsideMenu) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        focusWithoutPageScroll(triggerRef.current);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    focusWithoutPageScroll(searchRef.current);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [isOpen, positionPopover]);

  const toggle = value => {
    onSelectionChange(selectedSet.has(value)
      ? selectedIds.filter(idValue => idValue !== value)
      : [...selectedIds, value]);
  };

  return (
    <div className="report-dimension-filter" data-testid={`report-filter-${id}`}>
      <div className="report-dimension-filter__heading">
        <label htmlFor={`${id}-mode`} id={`${id}-label`}><FormattedMessage id={labelId}/></label>
        {selectedIds.length > 0 && (
          <span className="report-dimension-filter__count" data-testid={`${id}-selected-count`}>
            {selectedIds.length}
          </span>
        )}
      </div>
      <div className={`report-dimension-filter__controls${mode === 'all' ? ' report-dimension-filter__controls--all' : ''}`}>
        <select
          aria-label={`${id} mode`}
          id={`${id}-mode`}
          onChange={event => onModeChange(event.target.value)}
          value={mode}
        >
          <FormattedMessage id="components.pages.reports.filters.all">
            {message => <option value="all">{message}</option>}
          </FormattedMessage>
          <FormattedMessage id="components.pages.reports.filters.include">
            {message => <option value="include">{message}</option>}
          </FormattedMessage>
          <FormattedMessage id="components.pages.reports.filters.exclude">
            {message => <option value="exclude">{message}</option>}
          </FormattedMessage>
        </select>
        {mode !== 'all' && <div className="report-filter-picker" ref={pickerRef}>
          <FormattedMessage id="components.pages.reports.filters.choose">
            {message => (
              <button
                aria-controls={`${id}-options`}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-label={`${id} selector`}
                className="report-filter-picker__trigger"
                onClick={() => setIsOpen(open => !open)}
                ref={triggerRef}
                title={selectedLabels.length > 0 ? selectedLabels.join(', ') : message}
                type="button"
              >
                <span>
                  {selectedLabels.length === 0 && message}
                  {selectedLabels.length === 1 && selectedLabels[0]}
                  {selectedLabels.length > 1 && `${selectedLabels[0]} +${selectedLabels.length - 1}`}
                </span>
              </button>
            )}
          </FormattedMessage>
        </div>}
      </div>
      {isOpen && mode !== 'all' && typeof document !== 'undefined' && createPortal(
            <div
              aria-label={`${id} options`}
              className="report-filter-picker__menu"
              id={`${id}-options`}
              ref={menuRef}
              role="dialog"
              style={popoverStyle}
            >
              <FormattedMessage id="components.pages.reports.filters.search">
                {message => (
                  <input
                    aria-label={`${id} search`}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={message}
                    ref={searchRef}
                    type="search"
                    value={search}
                  />
                )}
              </FormattedMessage>
              <div
                className="report-filter-picker__options"
                style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
              >
                {groupedOptions.map((category, categoryIndex) => {
                  const categoryLabelId = `${id}-category-${categoryIndex}`;
                  return (
                    <div
                      aria-labelledby={category.label ? categoryLabelId : undefined}
                      className="report-filter-picker__category"
                      key={category.key}
                      role={category.label ? 'group' : undefined}
                    >
                      {category.label && (
                        <div
                          className="report-filter-picker__category-label"
                          id={categoryLabelId}
                          role="heading"
                          aria-level="3"
                        >
                          {category.label}
                        </div>
                      )}
                      {category.groups.map((group, groupIndex) => {
                        const groupLabelId = `${id}-category-${categoryIndex}-group-${groupIndex}`;
                        return (
                          <div
                            aria-labelledby={group.label ? groupLabelId : undefined}
                            className="report-filter-picker__group"
                            key={group.key}
                            role={group.label ? 'group' : undefined}
                          >
                            {group.label && (
                              <div className="report-filter-picker__group-label" id={groupLabelId}>
                                {group.label}
                              </div>
                            )}
                            {group.options.map((option, optionIndex) => {
                              const optionId = `${id}-option-${categoryIndex}-${groupIndex}-${optionIndex}`;
                              return (
                                <label htmlFor={optionId} key={option.value}>
                                  <input
                                    checked={selectedSet.has(option.value)}
                                    id={optionId}
                                    onChange={() => toggle(option.value)}
                                    type="checkbox"
                                  />
                                  <span>{option.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {filteredOptions.length === 0 && (
                  <span className="report-filter-picker__empty">
                    <FormattedMessage id={emptyMessageId}/>
                  </span>
                )}
              </div>
            </div>
      , document.body)}
      {selectedIds.length > 0 && (
        <div className="report-filter-chips" aria-label={`${id} selected`}>
          {selectedIds.map(value => (
            <button
              className="report-filter-chip"
              key={value}
              onClick={() => toggle(value)}
              title={labelsById[value] || value}
              type="button"
            >
              <span>{labelsById[value] || value}</span>
              <i aria-hidden="true" className="fas fa-times"/>
            </button>
          ))}
          <button
            className="report-filter-clear"
            onClick={() => onSelectionChange([])}
            type="button"
          >
            <FormattedMessage id="components.pages.reports.filters.clear"/>
          </button>
        </div>
      )}
    </div>
  );
}

export { ReportDimensionFilter };
